import { useCallback, useEffect, useMemo } from 'react';

import type { SubscriptionCreditCard } from '@order/graphql';
import {
  useGetSubscriptionPaymentMethodQuery,
  useRemoveSubscriptionPaymentMethodMutation,
  useUpdateSubscriptionPaymentMethodMutation,
} from '@order/graphql';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';

export const useSweetpassPaymentMethodEdit = ({
  paymentMethodId,
}: UseSweetpassPaymentMethodEdit) => {
  const { t } = useLocalizationContext();

  const isSweetpassEnabled = useFeatureFlag('OPS-sweetpass-enabled', {
    listenForChanges: true,
  });

  const [{ fetching: isUpdateFetching }, updateSubscriptionPaymentMethod] =
    useUpdateSubscriptionPaymentMethodMutation();
  const [{ fetching: isRemoveFetching }, removeSubscriptionPaymentMethod] =
    useRemoveSubscriptionPaymentMethodMutation();

  const [
    {
      data: sweetpassPaymentMethodResponseData,
      fetching: isSweetpassPaymentMethodFetching,
    },
    getSubscriptionPaymentMethod,
  ] = useGetSubscriptionPaymentMethodQuery({
    pause: true,
  });

  const isSweetpassPaymentMethodQueryResolved =
    sweetpassPaymentMethodResponseData !== undefined;

  const shouldPerformQuery =
    isSweetpassEnabled &&
    !isSweetpassPaymentMethodFetching &&
    !isSweetpassPaymentMethodQueryResolved &&
    Boolean(paymentMethodId);

  const subscriptionId =
    sweetpassPaymentMethodResponseData?.getSubscriptions[0]?.id;
  const paymentMethod =
    sweetpassPaymentMethodResponseData?.paymentSubscriptionProfile?.creditCards?.find(
      (creditCard) => creditCard.id === paymentMethodId,
    );

  useEffect(() => {
    if (!shouldPerformQuery) return;
    getSubscriptionPaymentMethod();
  }, [
    shouldPerformQuery,
    isSweetpassPaymentMethodQueryResolved,
    getSubscriptionPaymentMethod,
  ]);

  const setActive = useCallback(async () => {
    if (!subscriptionId) return;
    const { data, error } = await updateSubscriptionPaymentMethod({
      subscriptionId,
      paymentMethodId,
    });

    if (error) {
      throw new Error(error.message);
    }

    if (
      !data ||
      data.updateSubscriptionPaymentMethod.__typename !==
        'UpdateSubscriptionPaymentMethodSuccessResponse'
    ) {
      throw new Error(t('general.error'));
    }
  }, [t, paymentMethodId, subscriptionId, updateSubscriptionPaymentMethod]);

  const remove = useCallback(async () => {
    const { data, error } = await removeSubscriptionPaymentMethod({
      paymentMethodId,
    });

    if (error) {
      throw new Error(error.message);
    }

    if (
      data?.removeSubscriptionPaymentMethod.__typename ===
      'ActivePaymentMethodError'
    ) {
      throw new Error(
        t(
          'account.sweetpass-membership.payment-methods.cannot-remove-active-payment-method-error',
        ),
      );
    }

    if (
      !data ||
      data.removeSubscriptionPaymentMethod.__typename === 'UnknownUserError'
    ) {
      throw new Error(t('general.error'));
    }
  }, [t, paymentMethodId, removeSubscriptionPaymentMethod]);

  return useMemo(
    () => ({
      isSweetpassEnabled,
      hasActiveSubscription: Boolean(subscriptionId),
      paymentMethod,
      setActive,
      remove,
      isSettingActive: isUpdateFetching,
      isRemoving: isRemoveFetching,
      isSweetpassPaymentMethodLoading: !isSweetpassPaymentMethodQueryResolved,
    }),
    [
      isSweetpassEnabled,
      subscriptionId,
      paymentMethod,
      setActive,
      remove,
      isUpdateFetching,
      isRemoveFetching,
      isSweetpassPaymentMethodQueryResolved,
    ],
  );
};

export type UseSweetpassPaymentMethodEdit = {
  readonly paymentMethodId: string;
};

export type PaymentMethod = NonNullable<SubscriptionCreditCard> | undefined;
